/**
 * Carte d'indicateur à deux valeurs : deux chiffres qui se lisent ensemble,
 * dans une seule carte coupée en son milieu.
 *
 * Pourquoi une carte plutôt que deux. Certains chiffres ne veulent rien dire
 * l'un sans l'autre — ce qu'on a financé et ce qu'il reste à facturer dessus.
 * Séparés dans la rangée, l'œil doit les rapprocher lui-même ; côte à côte, la
 * comparaison est immédiate.
 *
 * Ce qu'elle garde d'`Indicateur` : le fond neutre, et la couleur réduite au
 * chiffre et à un liseré. Ici il y en a deux — le bord gauche porte l'accent de
 * la moitié gauche, le trait du milieu celui de la moitié droite. Chaque moitié
 * reste cliquable pour son compte : les deux chiffres ne mènent pas au même
 * écran, et une carte qui n'aurait qu'une destination mentirait sur l'un des
 * deux.
 *
 * Elle occupe deux colonnes de la grille, celles des deux cartes qu'elle
 * remplace : la rangée garde le même nombre de colonnes, donc le même
 * alignement avec les rangées voisines.
 */
import React, { useState } from 'react';
import { C, E, RAYON, T, ACCENTS } from '../theme';

function Moitie({ donnee, teinte, aligneDroite }) {
  const [survol, setSurvol] = useState(false);
  const cliquable = typeof donnee.onClick === 'function';

  return (
    <div
      onClick={donnee.onClick}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      style={{
        display: 'flex', flexDirection: 'column', gap: '2px',
        minWidth: 0,
        cursor: cliquable ? 'pointer' : 'default',
        // Le survol n'éclaire que la moitié visée : c'est ce qui montre que
        // les deux chiffres ne partent pas au même endroit.
        background: survol && cliquable ? teinte.fond : 'transparent',
        borderRadius: RAYON.sm,
        margin: `-${E.xs}`, padding: E.xs,
        transition: 'background 120ms ease',
        paddingLeft: aligneDroite ? E.md : E.xs,
      }}
    >
      <div style={{ ...T.etiquette, color: C.texteMoyen, display: 'flex',
                    alignItems: 'center', gap: E.xs }} translate="no">
        {donnee.libelle}
        {cliquable && (
          <span style={{ color: teinte.trait, opacity: survol ? 1 : 0,
                         transition: 'opacity 120ms ease' }}>→</span>
        )}
      </div>
      <div style={{ ...T.chiffre, color: teinte.texte, fontSize: '22px' }}>
        {donnee.valeur}
      </div>
      {donnee.precision && (
        <div style={{ fontSize: '11.5px', color: C.texteFaible, lineHeight: 1.35 }}
             translate="no">
          {donnee.precision}
        </div>
      )}
    </div>
  );
}

export default function IndicateurDouble({ gauche, droite }) {
  const teinteGauche = ACCENTS[gauche.accent] || ACCENTS.marine;
  const teinteDroite = ACCENTS[droite.accent] || ACCENTS.marine;

  return (
    <div
      style={{
        gridColumn: 'span 2',
        background: C.surface,
        border: `1px solid ${C.bordure}`,
        borderLeft: `3px solid ${teinteGauche.trait}`,
        borderRadius: RAYON.lg,
        padding: `${E.md} ${E.lg}`,
        display: 'grid', gridTemplateColumns: '1fr 1px 1fr',
        gap: E.md,
        alignItems: 'start',
        minWidth: 0,
      }}
    >
      <Moitie donnee={gauche} teinte={teinteGauche} />
      {/* Le trait du milieu tient lieu de liseré pour la moitié droite : sans
          lui, seule la gauche porterait sa couleur. */}
      <div style={{ background: teinteDroite.trait, alignSelf: 'stretch',
                    borderRadius: '1px', opacity: 0.55 }} />
      <Moitie donnee={droite} teinte={teinteDroite} aligneDroite />
    </div>
  );
}
